import type {
  FilterConditionPluginSpecification,
} from '@bindoc-projects/filter-condition-domain-lib';
import {
  FilterConditionPluginImpl
} from '@bindoc-projects/filter-condition-domain-lib';

import type {
  BdCondition,
} from '@bindoc-projects/condition-domain-lib';
import {
  BdContentCondition,
} from '@bindoc-projects/condition-domain-lib';
import { BdArrayUtil } from '@bindoc-projects/util-domain-lib';

export const INTUITIVE_FILE_OF_TRUTH_PROCEDURE_SUBJECT_CONDITION_ID =
  'intuitiveFotProcedureSubject';
export const FilterConditionIntuitiveFileOfTruthProcedureSubjectPluginType: FilterConditionPluginSpecification<
  Omit<JsonIntuitiveFileOfTruthProcedureSubjectPluginDto, 'type'>,
  JsonIntuitiveFileOfTruthProcedureSubjectPluginDto,
  IntuitiveFileOfTruthProcedureSubjectCondition
> = {
  type: 'intuitiveFotProcedureSubject',
  category: 'case',
  displayName: {
    de: 'Intuitive File of Truth Procedure Subject',
    en: 'Intuitive File of Truth Procedure Subject',
  },
  sortingIndex: 8,
  occurrence: 'single',
  newInstance(): FilterConditionIntuitiveFileOfTruthProcedureSubjectPlugin {
    return new FilterConditionIntuitiveFileOfTruthProcedureSubjectPlugin([], null);
  },
  fromCondition(
    jsonObj: IntuitiveFileOfTruthProcedureSubjectCondition,
  ): FilterConditionPluginImpl<
      Omit<JsonIntuitiveFileOfTruthProcedureSubjectPluginDto, 'type'>
    > {
    return new FilterConditionIntuitiveFileOfTruthProcedureSubjectPlugin(
        jsonObj.includes ? jsonObj.includes : null,
        jsonObj.excludes ? jsonObj.excludes : null,
      );
  },
  fromObject(
    obj: JsonIntuitiveFileOfTruthProcedureSubjectPluginDto,
  ): FilterConditionPluginImpl<
      Omit<JsonIntuitiveFileOfTruthProcedureSubjectPluginDto, 'type'>
    > {
    return new FilterConditionIntuitiveFileOfTruthProcedureSubjectPlugin(
        obj.includes ? obj.includes : null,
        obj.excludes ? obj.excludes : null,
      );
  },
};

export class FilterConditionIntuitiveFileOfTruthProcedureSubjectPlugin extends FilterConditionPluginImpl<
  Omit<JsonIntuitiveFileOfTruthProcedureSubjectPluginDto, 'type'>,
  JsonIntuitiveFileOfTruthProcedureSubjectPluginDto
> {
  public static override Specification: FilterConditionPluginSpecification<
    Omit<JsonIntuitiveFileOfTruthProcedureSubjectPluginDto, 'type'>,
    JsonIntuitiveFileOfTruthProcedureSubjectPluginDto,
    IntuitiveFileOfTruthProcedureSubjectCondition
  > = FilterConditionIntuitiveFileOfTruthProcedureSubjectPluginType;

  constructor(
    protected includes: string[],
    protected excludes: string[],
  ) {
    super(FilterConditionIntuitiveFileOfTruthProcedureSubjectPluginType.type);
  }

  override equals(
    other: FilterConditionIntuitiveFileOfTruthProcedureSubjectPlugin,
  ): boolean {
    return (
      super.equals(other) &&
      BdArrayUtil.equals(this.includes, other.includes) &&
      BdArrayUtil.equals(this.excludes, other.excludes)
    );
  }

  clone(): FilterConditionIntuitiveFileOfTruthProcedureSubjectPlugin {
    return new FilterConditionIntuitiveFileOfTruthProcedureSubjectPlugin(
      this.includes ? [...this.includes] : null,
      this.excludes ? [...this.excludes] : null,
    );
  }

  toCondition(): BdCondition | null {
    return new IntuitiveFileOfTruthProcedureSubjectCondition(
        this.includes,
        this.excludes,
      );
  }

  toJSON(): JsonIntuitiveFileOfTruthProcedureSubjectPluginDto | null {
    return {
      type: this.type,
      includes: this.includes ? this.includes : null,
      excludes: this.excludes ? this.excludes : null,
    };
  }

  toValue(): Omit<JsonIntuitiveFileOfTruthProcedureSubjectPluginDto, 'type'> {
    return {
      includes: this.includes,
      excludes: this.excludes,
    };
  }

  override valid(): boolean {
    return (
      (this.includes && this.includes.length > 0) ||
      (this.excludes && this.excludes.length > 0)
    );
  }

  validationToText(): string {
    if (!this.includes && !this.excludes) {
      return 'Mindestens eine Intuitive File of Truth Procedure Subject auswählen';
    }

    if (this.includes && this.includes.length === 0) {
      return 'Mindestens eine Intuitive File of Truth Procedure Subject auswählen';
    } else if (this.excludes && this.excludes.length === 0) {
      return 'Mindestens eine Intuitive File of Truth Procedure Subject auswählen';
    } else {
      return '';
    }
  }

  toText(): string | null {
    if (!this.includes && !this.excludes) {
      return '';
    }

    if (this.excludes) {
      return this.excludes.length === 1
        ? 'ein Intuitive File of Truth Procedure Subject'
        : `${this.excludes.length} Intuitive File of Truth Procedure Subjects`;
    }

    return this.includes.length === 1
      ? 'ein Intuitive File of Truth Procedure Subject'
      : `${this.includes.length} Intuitive File of Truth Procedure Subjects`;
  }
}

class IntuitiveFileOfTruthProcedureSubjectCondition extends BdContentCondition<string> {
  constructor(includes: string[], excludes: string[]) {
    super(
      INTUITIVE_FILE_OF_TRUTH_PROCEDURE_SUBJECT_CONDITION_ID,
      includes,
      excludes,
    );
  }
}

interface JsonIntuitiveFileOfTruthProcedureSubjectPluginDto {
  type: string;
  includes: string[];
  excludes: string[];
}
